import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import {
  Robot, Lightning, MagnifyingGlass, Sparkle, Plus, Trash, Terminal,
  FloppyDisk, Archive, ClipboardText, DownloadSimple, CheckSquare, Square,
} from "@phosphor-icons/react";

const labelCls = "block font-mono text-[10px] uppercase tracking-wider text-[#71717A] mb-2";
const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";

export default function AutoPkg() {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("addigy");
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]); // [{id?, name, recipe, repo}]
  const [customName, setCustomName] = useState("");
  const [resolving, setResolving] = useState(false);

  const [destination, setDestination] = useState("package");
  const [githubToken, setGithubToken] = useState("");
  const [munkiRepo, setMunkiRepo] = useState("");
  const [jssUrl, setJssUrl] = useState("");
  const [jssUser, setJssUser] = useState("");
  const [jssPass, setJssPass] = useState("");
  const [failTrust, setFailTrust] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const [hour, setHour] = useState(4);
  const [slack, setSlack] = useState("");

  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState(0);
  const [projects, setProjects] = useState([]);
  const [saveTarget, setSaveTarget] = useState("new");
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/autopkg/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const isSelected = (recipe) => selected.some((s) => s.recipe === recipe);
  const toggle = (app) => {
    setSelected((prev) => prev.some((s) => s.recipe === app.recipe)
      ? prev.filter((s) => s.recipe !== app.recipe)
      : [...prev, { name: app.name, recipe: app.recipe, repo: app.repo }]);
  };
  const removeSel = (recipe) => setSelected((prev) => prev.filter((s) => s.recipe !== recipe));

  const resolveCustom = async () => {
    if (!customName.trim()) { toast.error("Type an app name to resolve"); return; }
    setResolving(true);
    try {
      const { data } = await api.post("/autopkg/resolve", { app_name: customName.trim() });
      if (isSelected(data.recipe)) { toast.message(`${data.name} already added`); }
      else { setSelected((prev) => [...prev, data]); toast.success(`Resolved: ${data.recipe} (${data.repo})`); }
      setCustomName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resolve that app");
    } finally { setResolving(false); }
  };

  const build = async () => {
    if (!selected.length) { toast.error("Pick at least one app to auto-update"); return; }
    setBuilding(true);
    try {
      const params = {
        destination, recipes: selected, github_token: githubToken, munki_repo: munkiRepo,
        jss_url: jssUrl, jss_user: jssUser, jss_pass: jssPass, fail_without_trust: failTrust,
        schedule, schedule_hour: Number(hour), slack_webhook: slack,
      };
      const { data } = await api.post("/autopkg/build", { provider, params });
      setResult(data); setTab(0);
      toast.success("AutoPkg pipeline generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally { setBuilding(false); }
  };

  const artifacts = result?.artifacts || [];
  const active = artifacts[tab];

  const copyOut = async () => {
    try { await navigator.clipboard.writeText(active.content); toast.success("Copied"); }
    catch { toast.error("Copy failed — select manually"); }
  };
  const downloadOut = () => {
    const blob = new Blob([active.content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = active.filename; a.click();
  };
  const save = async () => {
    const script = artifacts.find((a) => a.type === "script");
    if (!script) return;
    setSaving(true);
    try {
      const body = { title: `AutoPkg Autopilot (${destination})`, provider, output_type: "script",
        content: script.content, language: "bash", filename: script.filename, deploy_notes: result.deploy_notes, prompt: "" };
      if (saveTarget === "new") await api.post("/projects", body);
      else await api.post(`/projects/${saveTarget}/versions`, { ...body, note: "AutoPkg" });
      toast.success("Saved to projects");
      api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const downloadBundle = async () => {
    if (!artifacts.length) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle-multi`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ title: "AutoPkg Autopilot", provider, deploy_notes: result.deploy_notes,
          files: artifacts.map((a) => ({ filename: a.filename, content: a.content })) }),
      });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `${provider}-autopkg-autopilot.zip`; a.click();
      toast.success("Bundle downloaded");
    } catch { toast.error("Bundle failed"); }
    finally { setBundling(false); }
  };

  const filtered = catalog.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.recipe.toLowerCase().includes(search.toLowerCase()));

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl tracking-tight flex items-center gap-2">
            <Robot size={22} weight="fill" className="text-[#00F0FF]" /> AutoPkg Autopilot
          </h1>
          <p className="text-sm text-[#A1A1AA] font-mono mt-1">
            Pick the apps you want auto-updated → get a complete, ready-to-run AutoPkg patch pipeline.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-px bg-[#27272A]">
        <div className="bg-[#0A0A0B] p-8 space-y-6">
          <div>
            <label className={labelCls}>MDM Provider</label>
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <button key={p.id} data-testid={`ap-provider-${p.id}`} onClick={() => setProvider(p.id)}
                  className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}
                </button>
              ))}
            </div>
          </div>

          {/* App picker */}
          <div>
            <label className={labelCls}>Apps to auto-update</label>
            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input data-testid="ap-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search apps…" className={inputCls + " pl-8"} />
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1" data-testid="ap-catalog">
              {filtered.map((app) => (
                <button key={app.id} data-testid={`ap-app-${app.id}`} onClick={() => toggle(app)}
                  className={`flex items-center gap-2 px-3 py-2 border text-left text-xs transition-colors ${isSelected(app.recipe) ? "border-[#00F0FF] bg-[#00F0FF]/5 text-[#FAFAFA]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"}`}>
                  {isSelected(app.recipe) ? <CheckSquare size={16} weight="fill" className="text-[#00F0FF] shrink-0" /> : <Square size={16} className="text-[#52525B] shrink-0" />}
                  <span className="truncate">{app.name}</span>
                </button>
              ))}
            </div>

            {/* AI resolve custom */}
            <div className="mt-3 flex gap-2">
              <input data-testid="ap-custom-input" value={customName} onChange={(e) => setCustomName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && resolveCustom()}
                placeholder="Other app? Type its name and let AI find the recipe…" className={inputCls} />
              <button data-testid="ap-resolve-btn" onClick={resolveCustom} disabled={resolving}
                className="flex items-center gap-1.5 border border-[#B37FEB] text-[#C79BF0] px-3 py-2 text-xs whitespace-nowrap hover:bg-[#B37FEB]/10 transition-colors disabled:opacity-50">
                <Sparkle size={14} weight="fill" /> {resolving ? "Finding…" : "AI find"}
              </button>
            </div>

            {selected.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="ap-selected">
                {selected.map((s) => (
                  <span key={s.recipe} className="flex items-center gap-1.5 border border-[#27272A] bg-[#0f0f11] px-2.5 py-1.5 text-[11px] font-mono">
                    <span className="text-[#FAFAFA]">{s.name}</span>
                    <span className="text-[#52525B]">{s.recipe}</span>
                    <button data-testid={`ap-remove-${s.recipe}`} onClick={() => removeSel(s.recipe)} className="text-[#71717A] hover:text-[#FF5F56]"><Trash size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Destination */}
          <div>
            <label className={labelCls}>Destination</label>
            <select data-testid="ap-destination" value={destination} onChange={(e) => setDestination(e.target.value)} className={inputCls}>
              <option value="package">Package only (download &amp; build)</option>
              <option value="munki">Import into Munki</option>
              <option value="jamf">Upload to Jamf (JamfUploader)</option>
            </select>
          </div>

          {(destination === "munki" || destination === "jamf") && (
            <div>
              <label className={labelCls}>Munki repo path</label>
              <input data-testid="ap-munki-repo" value={munkiRepo} onChange={(e) => setMunkiRepo(e.target.value)} className={inputCls} placeholder="/Volumes/munki_repo" />
            </div>
          )}
          {destination === "jamf" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><label className={labelCls}>Jamf URL</label><input data-testid="ap-jss-url" value={jssUrl} onChange={(e) => setJssUrl(e.target.value)} className={inputCls} placeholder="https://acme.jamfcloud.com" /></div>
              <div><label className={labelCls}>API user</label><input data-testid="ap-jss-user" value={jssUser} onChange={(e) => setJssUser(e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>API password</label><input data-testid="ap-jss-pass" type="password" value={jssPass} onChange={(e) => setJssPass(e.target.value)} className={inputCls} /></div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className={labelCls}>GitHub API token (optional, raises rate limits)</label><input data-testid="ap-github-token" value={githubToken} onChange={(e) => setGithubToken(e.target.value)} className={inputCls} placeholder="ghp_…" /></div>
            <div><label className={labelCls}>Slack webhook (optional notifications)</label><input data-testid="ap-slack" value={slack} onChange={(e) => setSlack(e.target.value)} className={inputCls} placeholder="https://hooks.slack.com/…" /></div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="ap-fail-trust" onClick={() => setFailTrust(!failTrust)}
              className={`px-3 py-2 border text-xs ${failTrust ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#71717A]"}`}>Fail on untrusted recipes</button>
            <button data-testid="ap-schedule" onClick={() => setSchedule(!schedule)}
              className={`px-3 py-2 border text-xs ${schedule ? "border-[#00FF41] text-[#00FF41]" : "border-[#27272A] text-[#71717A]"}`}>Daily scheduled run</button>
            {schedule && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-[#71717A]">at</span>
                <input data-testid="ap-hour" type="number" min="0" max="23" value={hour} onChange={(e) => setHour(e.target.value)} className={inputCls + " w-20"} />
                <span className="text-[10px] font-mono text-[#71717A]">:00</span>
              </div>
            )}
          </div>

          <button data-testid="ap-build-btn" onClick={build} disabled={building}
            className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-5 py-2.5 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
            <Lightning size={16} weight="fill" /> {building ? "Building…" : "Build patch pipeline"}
          </button>
        </div>

        {/* Output */}
        <div className="bg-[#0A0A0B] p-8">
          <div className="flex items-center gap-2 mb-4 font-mono text-xs text-[#71717A] uppercase tracking-wider">
            <Terminal size={14} /> Output
          </div>
          {!result && <p className="text-sm text-[#71717A] font-mono">Pick apps and build to generate your AutoPkg pipeline.</p>}
          {result && active && (
            <div data-testid="ap-output">
              <div className="flex gap-1 mb-3 border-b border-[#27272A]">
                {artifacts.map((a, i) => (
                  <button key={i} data-testid={`ap-tab-${i}`} onClick={() => setTab(i)}
                    className={`px-3 py-2 text-xs font-mono border-b-2 -mb-px ${tab === i ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#71717A]"}`}>{a.label}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 mb-3">
                <button data-testid="ap-copy-btn" onClick={copyOut} className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-xs hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><ClipboardText size={13} /> Copy</button>
                <button data-testid="ap-download-btn" onClick={downloadOut} className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-xs hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><DownloadSimple size={13} /> Download</button>
              </div>
              <pre className="bg-[#050505] border border-[#27272A] p-4 overflow-auto max-h-[360px] font-mono text-[11px] text-[#C7C7CC] leading-relaxed whitespace-pre-wrap">{active.content}</pre>
              <p className="text-[10px] font-mono text-[#71717A] mt-3">{result.deploy_notes}</p>
              <select data-testid="ap-save-target" value={saveTarget} onChange={(e) => setSaveTarget(e.target.value)} className={inputCls + " mt-4"}>
                <option value="new">+ New project</option>
                {projects.map((p) => (<option key={p.id} value={p.id}>Add version to: {p.title}</option>))}
              </select>
              <div className="flex items-center gap-2 mt-2">
                <button data-testid="ap-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors disabled:opacity-50"><FloppyDisk size={16} /> {saving ? "…" : "Save"}</button>
                <button data-testid="ap-bundle-btn" onClick={downloadBundle} disabled={bundling} className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors disabled:opacity-50"><Archive size={16} /> {bundling ? "…" : "Bundle all"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
